import { RefObject, useEffect } from "react";

import { isTourInteractiveTarget } from "@/components/onboarding/utils/tourInteractive";

const isInsideElement = (
  element: HTMLElement | null,
  target: EventTarget | null
) => target instanceof Node && Boolean(element?.contains(target));

const useOnClickOutside = (
  ref: RefObject<HTMLElement>,
  callback: () => void,
  elements: Array<HTMLElement | null> = [],
  shouldRun: boolean = true, // Added parameter with default true
  // Dismiss events. Default = release (mouseup/touchend); pass the press events
  // (mousedown/touchstart) for panels with drag controls so a drag starting
  // inside isn't treated as an outside click on release.
  events: string[] = ["mouseup", "touchend"]
) => {
  useEffect(
    () => {
      if (shouldRun) {
        /* eslint-disable  @typescript-eslint/no-explicit-any */
        const listener = (event: any) => {
          // Do nothing if clicking ref's element or descendent elements
          if (
            ref.current &&
            !isInsideElement(ref.current, event.target) &&
            !elements.some((element) =>
              isInsideElement(element, event.target)
            ) &&
            !isTourInteractiveTarget(event.target)
          )
            callback();
        };
        events.forEach((event) => document.addEventListener(event, listener));
        return () => {
          events.forEach((event) =>
            document.removeEventListener(event, listener)
          );
        };
      }
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, callback, elements, shouldRun, events]
  );
};

export default useOnClickOutside;
